import { type TenancyMode } from "@nexys/user-management-client";
import { AlertCircle, Plus } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { errorMessage } from "../../lib/error-message.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Button } from "../ui/button.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog.js";
import { Field } from "../ui/field.js";
import { Input } from "../ui/input.js";
import { Label } from "../ui/label.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select.js";
import { Switch } from "../ui/switch.js";

export interface ProjectCreateDialogProps {
  /** Override the trigger button. */
  trigger?: ReactNode;
  onCreated?: (project: { id: string; name: string; slug: string }) => void;
}

export const ProjectCreateDialog = ({ trigger, onCreated }: ProjectCreateDialogProps) => {
  const client = useUmClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [tenancyMode, setTenancyMode] = useState<TenancyMode>("multi");
  const [allowSignup, setAllowSignup] = useState(false);
  const [allowTenantCreation, setAllowTenantCreation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName("");
    setSlug("");
    setOwnerEmail("");
    setTenancyMode("multi");
    setAllowSignup(false);
    setAllowTenantCreation(false);
    setError(null);
    setLoading(false);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const res = await client.createProject({
      name: name.trim(),
      slug: slug.trim() || undefined,
      ownerEmail: ownerEmail.trim() || undefined,
      tenancyMode,
      allowSignup,
      allowTenantCreation,
    });
    setLoading(false);
    if (res.ok) {
      onCreated?.({ id: res.data.id, name: res.data.name, slug: res.data.slug });
      reset();
      setOpen(false);
    } else {
      setError(errorMessage(res.error, "Couldn't create product."));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <DialogTrigger asChild>
        {trigger ?? (
          <Button>
            <Plus />
            New product
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create a new product</DialogTitle>
          <DialogDescription>
            Each product is an isolated user-management space (project) with
            its own tenancy configuration.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4" noValidate>
          <Field label="Name" htmlFor="project-name" required>
            <Input
              id="project-name"
              autoFocus
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Digis"
            />
          </Field>
          <Field
            label="Slug"
            htmlFor="project-slug"
            description="URL-safe identifier. Leave blank to derive from the name."
          >
            <Input
              id="project-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/\s+/g, "-"))}
              placeholder="digis"
            />
          </Field>
          <Field
            label="Owner"
            htmlFor="project-owner"
            description="Email of the account that will own this product. Leave blank to own it yourself."
          >
            <Input
              id="project-owner"
              type="email"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </Field>
          <Field
            label="Tenancy"
            htmlFor="project-tenancy"
            description="Single gives the product exactly one tenant, created now, which everyone who joins the product is put in; multi allows any number."
          >
            <Select
              value={tenancyMode}
              onValueChange={(v) => setTenancyMode(v as TenancyMode)}
            >
              <SelectTrigger id="project-tenancy">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="multi">Multi-tenant</SelectItem>
                <SelectItem value="single">Single tenant</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-0.5">
              <Label htmlFor="project-allow-signup">Open sign-up</Label>
              <p className="text-muted-foreground text-xs">
                Allow self-service sign-up scoped to this product.
              </p>
            </div>
            <Switch
              id="project-allow-signup"
              checked={allowSignup}
              onCheckedChange={setAllowSignup}
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-0.5">
              <Label htmlFor="project-allow-tenants">Self-serve tenants</Label>
              <p className="text-muted-foreground text-xs">
                Allow any authenticated user to create a tenant inside this
                product.
              </p>
            </div>
            <Switch
              id="project-allow-tenants"
              checked={allowTenantCreation}
              onCheckedChange={setAllowTenantCreation}
            />
          </div>
          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" loading={loading} disabled={!name.trim()}>
              Create product
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
